import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
type Phase = 'self-baseline' | 'partner-baseline' | 'relationship-context' | 'friction-loop' | 'system-stability';
export function RelationshipDecodeInputs() {
  const navigate = useNavigate();
  const [showEntry, setShowEntry] = useState(true);
  const [currentPhase, setCurrentPhase] = useState<Phase>('self-baseline');
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showEndScreen, setShowEndScreen] = useState(false);
  const [formData, setFormData] = useState({
    // Self Baseline
    fullName: '',
    dateOfBirth: '',
    timeOfBirthOption: '',
    timeOfBirth: '',
    birthCity: '',
    birthCountry: '',
    // Partner Baseline
    partnerName: '',
    partnerDob: '',
    partnerTimeOfBirthOption: '',
    partnerTimeOfBirth: '',
    partnerBirthCity: '',
    partnerBirthCountry: '',
    // Relationship Context
    relationshipType: '',
    // Core Friction Loop
    trigger: '',
    yourResponse: [] as string[],
    theirResponse: '',
    outcome: '',
    // System Stability
    duration: '',
    underlyingPressure: ''
  });
  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  const handleMultiSelect = (field: string, value: string) => {
    setFormData(prev => {
      const current = prev[field as keyof typeof prev] as string[];
      if (current.includes(value)) {
        return {
          ...prev,
          [field]: current.filter(v => v !== value)
        };
      } else if (current.length < 2) {
        return {
          ...prev,
          [field]: [...current, value]
        };
      }
      return prev;
    });
  };
  const handleStartDiagnostic = () => {
    setShowEntry(false);
  };
  const handleNext = () => {
    // Self Baseline navigation
    if (currentPhase === 'self-baseline') {
      if (!formData.fullName || !formData.dateOfBirth || !formData.timeOfBirthOption || !formData.birthCity || !formData.birthCountry) return;
      if ((formData.timeOfBirthOption === 'exact' || formData.timeOfBirthOption === 'approximate') && !formData.timeOfBirth) return;
      setCurrentPhase('partner-baseline');
      setCurrentStep(1);
    }
    // Partner Baseline navigation
    else if (currentPhase === 'partner-baseline') {
      if (currentStep === 1 && !formData.partnerName) return;
      if (currentStep === 2 && (!formData.partnerDob || !formData.partnerTimeOfBirthOption)) return;
      if (currentStep === 2 && (formData.partnerTimeOfBirthOption === 'exact' || formData.partnerTimeOfBirthOption === 'approximate') && !formData.partnerTimeOfBirth) return;
      if (currentStep === 3 && (!formData.partnerBirthCity || !formData.partnerBirthCountry)) return;
      if (currentStep < 3) {
        setCurrentStep(currentStep + 1);
      } else {
        setCurrentPhase('relationship-context');
        setCurrentStep(1);
      }
    }
    // Relationship Context navigation
    else if (currentPhase === 'relationship-context') {
      if (!formData.relationshipType) return;
      setCurrentPhase('friction-loop');
      setCurrentStep(1);
    }
    // Friction Loop navigation
    else if (currentPhase === 'friction-loop') {
      if (currentStep === 1 && !formData.trigger) return;
      if (currentStep === 2 && formData.yourResponse.length === 0) return;
      if (currentStep === 3 && !formData.theirResponse) return;
      if (currentStep === 4 && !formData.outcome) return;
      if (currentStep < 4) {
        setCurrentStep(currentStep + 1);
      } else {
        setCurrentPhase('system-stability');
        setCurrentStep(1);
      }
    }
    // System Stability navigation
    else if (currentPhase === 'system-stability') {
      if (currentStep === 1 && !formData.duration) return;
      if (currentStep === 2 && !formData.underlyingPressure) return;
      if (currentStep < 2) {
        setCurrentStep(currentStep + 1);
      } else {
        setShowEndScreen(true);
      }
    }
  };
  const handleGenerateReport = () => {
    setIsGenerating(true);
    console.log('Relationship Decode inputs:', formData);
    setTimeout(() => {
      navigate('/relationship-decode-output');
    }, 3000);
  };
  // Options
  const relationshipTypeOptions = ['Romantic partner', 'Spouse / marriage', 'Long-term dating', 'Former partner', 'Emotionally close connection'];
  const triggerOptions = ['Specific person or behavior', 'Type of situation', 'Time pressure', 'Uncertainty', 'Responsibility or expectations'];
  const yourResponseOptions = ['Explain or justify', 'Withdraw', 'Accommodate', 'Push harder', 'Escalate emotionally'];
  const theirResponseOptions = ['Avoid or deflect', 'Push back', 'Shut down', 'Control the frame', 'Seek reassurance'];
  const outcomeOptions = ['Temporary peace', 'Emotional distance', 'Repeated conflict', 'Unresolved tension', 'Short-term closeness, long-term drift'];
  const durationOptions = ['Recent', 'Months', 'Years', 'On and off'];
  const underlyingPressureOptions = ['Fear', 'Inadequacy', 'Guilt', 'Resentment', 'Frustration'];
  if (showEntry) {
    return <>
        <Helmet data-id="element-3182">
          <title data-id="element-3183">Relationship Decode — Diagnostic Input | Lazy Monk Labs</title>
          <meta name="robots" content="noindex, nofollow" data-id="element-3184" />
        </Helmet>

        <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5] flex flex-col" data-id="element-3185">
          <header data-id="element-3186">
            <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-[#2B2B2B]" data-id="element-3187">
              <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between" data-id="element-3188">
                <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity" data-id="element-3189">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jXd6KyuddrgAfqwwkkpUrR/Lazy_Monk_Labs_Logo.png" alt="Lazy Monk Labs Logo" className="h-8 w-auto" width="32" height="32" data-id="element-3190" />
                  <span className="text-lg font-medium tracking-tight" data-id="element-3191">
                    Lazy Monk Labs
                  </span>
                </Link>
              </div>
            </nav>
          </header>

          <main className="flex-1 flex items-center justify-center px-6 pt-20 pb-16" data-id="element-3192">
            <div className="max-w-2xl mx-auto text-center w-full" data-id="element-3193">
              <h1 className="text-4xl md:text-5xl font-medium mb-6 leading-tight tracking-tight" data-id="element-3194">
                Relationship Decode — diagnostic input
              </h1>
              <p className="text-[#F5F5F5]/60 mb-12 leading-relaxed max-w-xl mx-auto" data-id="element-3195">
                Answering these helps map the interaction system accurately.
                <br data-id="element-3196" />
                This is not an evaluation of individuals.
              </p>
              <button onClick={handleStartDiagnostic} className="px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg" data-id="element-3197">
                Start Diagnostic
              </button>
            </div>
          </main>
        </div>
      </>;
  }
  if (isGenerating) {
    return <>
        <Helmet data-id="element-3198">
          <title data-id="element-3199">
            Generating Report — Relationship Decode | Lazy Monk Labs
          </title>
          <meta name="robots" content="noindex, nofollow" data-id="element-3200" />
        </Helmet>

        <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5] flex items-center justify-center px-6" data-id="element-3201">
          <div className="text-center" data-id="element-3202">
            <p className="text-xl text-[#F5F5F5]/70" data-id="element-3203">
              Analyzing interaction patterns...
            </p>
          </div>
        </div>
      </>;
  }
  if (showEndScreen) {
    return <>
        <Helmet data-id="element-3204">
          <title data-id="element-3205">
            Ready to Generate — Relationship Decode | Lazy Monk Labs
          </title>
          <meta name="robots" content="noindex, nofollow" data-id="element-3206" />
        </Helmet>

        <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5] flex flex-col" data-id="element-3207">
          <header data-id="element-3208">
            <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-[#2B2B2B]" data-id="element-3209">
              <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between" data-id="element-3210">
                <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity" data-id="element-3211">
                  <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jXd6KyuddrgAfqwwkkpUrR/Lazy_Monk_Labs_Logo.png" alt="Lazy Monk Labs Logo" className="h-8 w-auto" width="32" height="32" data-id="element-3212" />
                  <span className="text-lg font-medium tracking-tight" data-id="element-3213">
                    Lazy Monk Labs
                  </span>
                </Link>
              </div>
            </nav>
          </header>

          <main className="flex-1 flex items-center justify-center px-6 pt-20 pb-16" data-id="element-3214">
            <div className="max-w-2xl mx-auto text-center w-full" data-id="element-3215">
              <h1 className="text-4xl md:text-5xl font-medium mb-6 leading-tight tracking-tight" data-id="element-3216">
                Ready to generate your Relationship Decode
              </h1>
              <p className="text-[#F5F5F5]/60 mb-12 leading-relaxed" data-id="element-3217">
                Interaction patterns and constraints have been captured.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-3218">
                <button onClick={handleGenerateReport} className="px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg" data-id="element-3219">
                  Generate Full Report
                </button>
                <button onClick={() => {
                setShowEndScreen(false);
                setCurrentPhase('self-baseline');
                setCurrentStep(1);
              }} className="px-8 py-5 border border-[#F5F5F5]/30 text-[#F5F5F5]/70 font-medium text-sm tracking-wide hover:bg-[#F5F5F5]/5 transition-colors" data-id="element-3220">
                  Edit responses
                </button>
              </div>
            </div>
          </main>
        </div>
      </>;
  }
  return <>
      <Helmet data-id="element-3221">
        <title data-id="element-3222">Relationship Decode — Diagnostic Input | Lazy Monk Labs</title>
        <meta name="robots" content="noindex, nofollow" data-id="element-3223" />
      </Helmet>

      <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5] flex flex-col" data-id="element-3224">
        <header data-id="element-3225">
          <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-[#2B2B2B]" data-id="element-3226">
            <div className="max-w-7xl mx-auto px-6 py-4" data-id="element-3227">
              <p className="text-xs text-[#F5F5F5]/50 text-center tracking-wider" data-id="element-3228">
                {currentPhase === 'self-baseline' && 'Your baseline'}
                {currentPhase === 'partner-baseline' && 'Partner baseline'}
                {currentPhase === 'relationship-context' && 'Relationship context'}
                {currentPhase === 'friction-loop' && `Friction loop — Step ${currentStep} of 4`}
                {currentPhase === 'system-stability' && `System stability — Step ${currentStep} of 2`}
              </p>
            </div>
          </nav>
        </header>

        <main className="flex-1 flex items-center justify-center px-6 pt-20 pb-16" data-id="element-3229">
          <div className="max-w-2xl mx-auto w-full" data-id="element-3230">
            {/* Self Baseline */}
            {currentPhase === 'self-baseline' && <div data-id="element-3231">
                <h1 className="text-3xl md:text-4xl font-medium mb-4 text-center leading-tight tracking-tight" data-id="element-3232">
                  Your baseline
                </h1>
                <p className="text-sm text-[#F5F5F5]/50 text-center mb-12" data-id="element-3233">
                  This establishes your baseline response architecture.
                  <br data-id="element-3234" />
                  It is not used for prediction.
                </p>

                <div className="space-y-6 mb-12" data-id="element-3235">
                  <div data-id="element-3236">
                    <label htmlFor="fullName" className="block text-sm font-medium mb-2" data-id="element-3237">
                      Full Name
                    </label>
                    <input type="text" id="fullName" value={formData.fullName} onChange={e => handleInputChange('fullName', e.target.value)} placeholder="Your full name" required className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] placeholder-[#F5F5F5]/40 focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" data-id="element-3238" />
                  </div>

                  <div data-id="element-3239">
                    <label htmlFor="dateOfBirth" className="block text-sm font-medium mb-2" data-id="element-3240">
                      Date of Birth
                    </label>
                    <input type="date" id="dateOfBirth" value={formData.dateOfBirth} onChange={e => handleInputChange('dateOfBirth', e.target.value)} required className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" data-id="element-3241" />
                  </div>

                  <div data-id="element-3242">
                    <label htmlFor="timeOfBirthOption" className="block text-sm font-medium mb-2" data-id="element-3243">
                      Time of Birth
                    </label>
                    <select id="timeOfBirthOption" value={formData.timeOfBirthOption} onChange={e => handleInputChange('timeOfBirthOption', e.target.value)} required className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" data-id="element-3244">
                      <option value="" data-id="element-3245">Select option</option>
                      <option value="exact" data-id="element-3246">Exact</option>
                      <option value="approximate" data-id="element-3247">Approximate</option>
                      <option value="unknown" data-id="element-3248">Unknown</option>
                    </select>
                  </div>

                  {(formData.timeOfBirthOption === 'exact' || formData.timeOfBirthOption === 'approximate') && <div data-id="element-3249">
                      <label htmlFor="timeOfBirth" className="block text-sm font-medium mb-2" data-id="element-3250">
                        Time
                      </label>
                      <input type="time" id="timeOfBirth" value={formData.timeOfBirth} onChange={e => handleInputChange('timeOfBirth', e.target.value)} required className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" data-id="element-3251" />
                    </div>}

                  <div data-id="element-3252">
                    <label className="block text-sm font-medium mb-2" data-id="element-3253">
                      Place of Birth
                    </label>
                    <input type="text" id="birthCity" value={formData.birthCity} onChange={e => handleInputChange('birthCity', e.target.value)} placeholder="City" required className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] placeholder-[#F5F5F5]/40 focus:border-[#F5F5F5]/50 focus:outline-none transition-colors mb-4" data-id="element-3254" />
                    <input type="text" id="birthCountry" value={formData.birthCountry} onChange={e => handleInputChange('birthCountry', e.target.value)} placeholder="Country" required className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] placeholder-[#F5F5F5]/40 focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" data-id="element-3255" />
                  </div>
                </div>

                <button onClick={handleNext} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg" data-id="element-3256">
                  Continue
                </button>
              </div>}

            {/* Partner Baseline - Step 1: Name */}
            {currentPhase === 'partner-baseline' && currentStep === 1 && <div data-id="element-3257">
                <h1 className="text-3xl md:text-4xl font-medium mb-4 text-center leading-tight tracking-tight" data-id="element-3258">
                  Partner baseline
                </h1>
                <p className="text-sm text-[#F5F5F5]/50 text-center mb-12" data-id="element-3259">
                  This establishes the interaction reference.
                  <br data-id="element-3260" />
                  It is not used for prediction or compatibility scoring.
                </p>
                <div className="mb-12" data-id="element-3261">
                  <label htmlFor="partnerName" className="block text-sm font-medium mb-3" data-id="element-3262">
                    Partner name (or initials)
                  </label>
                  <input type="text" id="partnerName" value={formData.partnerName} onChange={e => handleInputChange('partnerName', e.target.value)} className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" placeholder="Enter name or initials" data-id="element-3263" />
                </div>
                <button onClick={handleNext} disabled={!formData.partnerName} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-3264">
                  Continue
                </button>
              </div>}

            {/* Partner Baseline - Step 2: Date and Time of Birth */}
            {currentPhase === 'partner-baseline' && currentStep === 2 && <div data-id="element-3265">
                <h1 className="text-3xl md:text-4xl font-medium mb-12 text-center leading-tight tracking-tight" data-id="element-3266">
                  Partner baseline
                </h1>
                <div className="space-y-6 mb-12" data-id="element-3267">
                  <div data-id="element-3268">
                    <label htmlFor="partnerDob" className="block text-sm font-medium mb-3" data-id="element-3269">
                      Date of birth
                    </label>
                    <input type="date" id="partnerDob" value={formData.partnerDob} onChange={e => handleInputChange('partnerDob', e.target.value)} className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" data-id="element-3270" />
                  </div>

                  <div data-id="element-3271">
                    <label htmlFor="partnerTimeOfBirthOption" className="block text-sm font-medium mb-3" data-id="element-3272">
                      Time of birth
                    </label>
                    <select id="partnerTimeOfBirthOption" value={formData.partnerTimeOfBirthOption} onChange={e => handleInputChange('partnerTimeOfBirthOption', e.target.value)} className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] focus:border-[#F5F5F5]/50 focus:outline-none transition-colors mb-3" data-id="element-3273">
                      <option value="" data-id="element-3274">Select option</option>
                      <option value="exact" data-id="element-3275">Exact</option>
                      <option value="approximate" data-id="element-3276">Approximate</option>
                      <option value="unknown" data-id="element-3277">Unknown</option>
                    </select>
                    {(formData.partnerTimeOfBirthOption === 'exact' || formData.partnerTimeOfBirthOption === 'approximate') && <input type="time" value={formData.partnerTimeOfBirth} onChange={e => handleInputChange('partnerTimeOfBirth', e.target.value)} className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" data-id="element-3278" />}
                  </div>
                </div>
                <button onClick={handleNext} disabled={!formData.partnerDob || !formData.partnerTimeOfBirthOption} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-3279">
                  Continue
                </button>
              </div>}

            {/* Partner Baseline - Step 3: Place of Birth */}
            {currentPhase === 'partner-baseline' && currentStep === 3 && <div data-id="element-3280">
                <h1 className="text-3xl md:text-4xl font-medium mb-12 text-center leading-tight tracking-tight" data-id="element-3281">
                  Partner baseline
                </h1>
                <div className="mb-12 space-y-4" data-id="element-3282">
                  <div data-id="element-3283">
                    <label htmlFor="partnerBirthCity" className="block text-sm font-medium mb-3" data-id="element-3284">
                      Place of birth
                    </label>
                    <input type="text" id="partnerBirthCity" value={formData.partnerBirthCity} onChange={e => handleInputChange('partnerBirthCity', e.target.value)} className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" placeholder="City" data-id="element-3285" />
                  </div>
                  <div data-id="element-3286">
                    <input type="text" id="partnerBirthCountry" value={formData.partnerBirthCountry} onChange={e => handleInputChange('partnerBirthCountry', e.target.value)} className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" placeholder="Country" data-id="element-3287" />
                  </div>
                </div>
                <button onClick={handleNext} disabled={!formData.partnerBirthCity || !formData.partnerBirthCountry} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-3288">
                  Continue
                </button>
              </div>}

            {/* Relationship Context */}
            {currentPhase === 'relationship-context' && <div data-id="element-3289">
                <h1 className="text-3xl md:text-4xl font-medium mb-12 text-center leading-tight tracking-tight" data-id="element-3290">
                  What best describes this relationship?
                </h1>
                <div className="space-y-4 mb-12" data-id="element-3291">
                  {relationshipTypeOptions.map((option, index) => <label key={index} className={`block border p-6 cursor-pointer transition-colors ${formData.relationshipType === option ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-3292">
                      <input type="radio" name="relationshipType" value={option} checked={formData.relationshipType === option} onChange={e => handleInputChange('relationshipType', e.target.value)} className="sr-only" data-id="element-3293" />
                      <span className="text-base" data-id="element-3294">{option}</span>
                    </label>)}
                </div>
                <button onClick={handleNext} disabled={!formData.relationshipType} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-3295">
                  Continue
                </button>
              </div>}

            {/* Friction Loop - Q1: Trigger */}
            {currentPhase === 'friction-loop' && currentStep === 1 && <div data-id="element-3296">
                <h1 className="text-3xl md:text-4xl font-medium mb-12 text-center leading-tight tracking-tight" data-id="element-3297">
                  What typically activates tension between you?
                </h1>
                <div className="space-y-4 mb-12" data-id="element-3298">
                  {triggerOptions.map((option, index) => <label key={index} className={`block border p-6 cursor-pointer transition-colors ${formData.trigger === option ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-3299">
                      <input type="radio" name="trigger" value={option} checked={formData.trigger === option} onChange={e => handleInputChange('trigger', e.target.value)} className="sr-only" data-id="element-3300" />
                      <span className="text-base" data-id="element-3301">{option}</span>
                    </label>)}
                </div>
                <button onClick={handleNext} disabled={!formData.trigger} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-3302">
                  Continue
                </button>
              </div>}

            {/* Friction Loop - Q2: Your Response */}
            {currentPhase === 'friction-loop' && currentStep === 2 && <div data-id="element-3303">
                <h1 className="text-3xl md:text-4xl font-medium mb-6 text-center leading-tight tracking-tight" data-id="element-3304">
                  What do you typically do when this happens?
                </h1>
                <p className="text-sm text-[#F5F5F5]/50 text-center mb-12" data-id="element-3305">
                  Select up to 2 responses
                </p>
                <div className="space-y-4 mb-12" data-id="element-3306">
                  {yourResponseOptions.map((option, index) => <label key={index} className={`block border p-6 cursor-pointer transition-colors ${formData.yourResponse.includes(option) ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-3307">
                      <input type="checkbox" name="yourResponse" value={option} checked={formData.yourResponse.includes(option)} onChange={e => handleMultiSelect('yourResponse', e.target.value)} className="sr-only" data-id="element-3308" />
                      <span className="text-base" data-id="element-3309">{option}</span>
                    </label>)}
                </div>
                <button onClick={handleNext} disabled={formData.yourResponse.length === 0} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-3310">
                  Continue
                </button>
              </div>}

            {/* Friction Loop - Q3: Their Response */}
            {currentPhase === 'friction-loop' && currentStep === 3 && <div data-id="element-3311">
                <h1 className="text-3xl md:text-4xl font-medium mb-12 text-center leading-tight tracking-tight" data-id="element-3312">
                  How do they usually respond?
                </h1>
                <div className="space-y-4 mb-12" data-id="element-3313">
                  {theirResponseOptions.map((option, index) => <label key={index} className={`block border p-6 cursor-pointer transition-colors ${formData.theirResponse === option ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-3314">
                      <input type="radio" name="theirResponse" value={option} checked={formData.theirResponse === option} onChange={e => handleInputChange('theirResponse', e.target.value)} className="sr-only" data-id="element-3315" />
                      <span className="text-base" data-id="element-3316">{option}</span>
                    </label>)}
                </div>
                <button onClick={handleNext} disabled={!formData.theirResponse} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-3317">
                  Continue
                </button>
              </div>}

            {/* Friction Loop - Q4: Outcome */}
            {currentPhase === 'friction-loop' && currentStep === 4 && <div data-id="element-3318">
                <h1 className="text-3xl md:text-4xl font-medium mb-12 text-center leading-tight tracking-tight" data-id="element-3319">
                  What usually happens after?
                </h1>
                <div className="space-y-4 mb-12" data-id="element-3320">
                  {outcomeOptions.map((option, index) => <label key={index} className={`block border p-6 cursor-pointer transition-colors ${formData.outcome === option ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-3321">
                      <input type="radio" name="outcome" value={option} checked={formData.outcome === option} onChange={e => handleInputChange('outcome', e.target.value)} className="sr-only" data-id="element-3322" />
                      <span className="text-base" data-id="element-3323">{option}</span>
                    </label>)}
                </div>
                <button onClick={handleNext} disabled={!formData.outcome} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-3324">
                  Continue
                </button>
              </div>}

            {/* System Stability - Q5: Duration */}
            {currentPhase === 'system-stability' && currentStep === 1 && <div data-id="element-3325">
                <h1 className="text-3xl md:text-4xl font-medium mb-12 text-center leading-tight tracking-tight" data-id="element-3326">
                  How long has this dynamic been present?
                </h1>
                <div className="space-y-4 mb-12" data-id="element-3327">
                  {durationOptions.map((option, index) => <label key={index} className={`block border p-6 cursor-pointer transition-colors ${formData.duration === option ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-3328">
                      <input type="radio" name="duration" value={option} checked={formData.duration === option} onChange={e => handleInputChange('duration', e.target.value)} className="sr-only" data-id="element-3329" />
                      <span className="text-base" data-id="element-3330">{option}</span>
                    </label>)}
                </div>
                <button onClick={handleNext} disabled={!formData.duration} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-3331">
                  Continue
                </button>
              </div>}

            {/* System Stability - Q6: Underlying Pressure */}
            {currentPhase === 'system-stability' && currentStep === 2 && <div data-id="element-3332">
                <h1 className="text-3xl md:text-4xl font-medium mb-12 text-center leading-tight tracking-tight" data-id="element-3333">
                  What feels most active underneath this dynamic?
                </h1>
                <div className="space-y-4 mb-12" data-id="element-3334">
                  {underlyingPressureOptions.map((option, index) => <label key={index} className={`block border p-6 cursor-pointer transition-colors ${formData.underlyingPressure === option ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-3335">
                      <input type="radio" name="underlyingPressure" value={option} checked={formData.underlyingPressure === option} onChange={e => handleInputChange('underlyingPressure', e.target.value)} className="sr-only" data-id="element-3336" />
                      <span className="text-base" data-id="element-3337">{option}</span>
                    </label>)}
                </div>
                <button onClick={handleNext} disabled={!formData.underlyingPressure} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-3338">
                  Continue
                </button>
              </div>}
          </div>
        </main>
      </div>
    </>;
}